import { ILayoutProps } from '@bridebook/web/next';
import { useTranslation } from 'next-i18next';
import { Router as NextRouter } from 'next/router';
import { FC, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import FullpageLoader from '@bridebook/web/app-shared/components/fullpage-loader/fullpage-loader';
import { AppBannerScript, MetaRouterScript } from '@bridebook/web/app-shared/lib/app/scripts';
import { useGlobalABTests } from '@bridebook/web/lib/ab-tests/hooks/use-global-ab-tests';
import {
  AppRenderingContextProvider,
  useCanRender,
} from '@bridebook/web/lib/app/app-rendering-context';
import ComponentLevelErrorBoundary from '@bridebook/web/lib/app/component-level-error-boundary';
import { env } from '@bridebook/web/lib/env';
import addUKPluralUpdate from '@bridebook/web/lib/i18n/addUKPluralUpdate';
import { getIsCordova } from '@bridebook/web/lib/mobile-app/selectors';
import { useSelector } from '@bridebook/web/lib/utils';
import Page, { IPageProps } from '../components/page';
import { alwaysLoadedNS } from './i18n/constants';

// Create a client

export interface LayoutFullProps extends PropsWithChildren {
  router: NextRouter;
  pageComponentProps: IPageProps;
  layoutProps: ILayoutProps;
}

export const PageWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { ready: translationsReady } = useTranslation(alwaysLoadedNS);
  const canRender = useCanRender();

  return (
    <>
      {canRender && translationsReady && children}
      {/* Dev only: the loader is server-rendered then swapped out during hydration,
        which Turbopack reports as a mismatch. Kept in built envs until the app-shell
        dynamic imports are sorted. LIVE-28703 */}
      {env.IS_PRODUCTION_BUILD && !canRender && <FullpageLoader />}
    </>
  );
};

export const LayoutFull = ({ children, pageComponentProps, router }: LayoutFullProps) => {
  addUKPluralUpdate();
  useGlobalABTests();
  const isCordova = useSelector(getIsCordova);
  const appStarted = useSelector((state) => state.app.started);
  const showReactQueryDevtools = Object.keys(router.query).includes('reactQuery');

  return (
    <AppRenderingContextProvider>
      {appStarted && (
        <>
          <AppBannerScript showBanner={!isCordova} />
          <MetaRouterScript isCordova={isCordova} />
        </>
      )}
      <ComponentLevelErrorBoundary component="LayoutFull">
        <PageWrapper>
          <Page {...pageComponentProps}>{children}</Page>
        </PageWrapper>
      </ComponentLevelErrorBoundary>
      {!env.IS_PRODUCTION_BUILD && showReactQueryDevtools && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </AppRenderingContextProvider>
  );
};

type IWithLayoutLogged = (
  page: ReactElement,
  props: LayoutFullProps,
  layoutProps: LayoutFullProps['layoutProps'],
  pageComponentProps?: LayoutFullProps['pageComponentProps'],
) => ReactElement;

export const withFullLayout: IWithLayoutLogged = (
  page,
  props,
  layoutProps,
  pageComponentProps = {},
) => (
  <LayoutFull
    layoutProps={layoutProps}
    router={props.router}
    pageComponentProps={pageComponentProps}>
    {page}
  </LayoutFull>
);
